import React, { useState, useEffect, useCallback } from 'react';
import { 
  getUserMemories, 
  createMemory, 
  updateMemory, 
  deleteMemory, 
  deleteMemories,
  getCategoryInfo, 
  getReplacedInfo,
  formatMemoryDate, 
  truncateUtterance,
  extractEntitiesFromMemory,
  getMemorySentiment,
  downloadMemoriesCSV,
  MEMORY_CATEGORIES 
} from '../../../../../services/memoryService';
import Pagination from '../../../Pagination';
import MemoryEditModal from '../modals/MemoryEditModal';

const MemoriesTab = ({ userId, memoryStats, onRefresh, dashboardMode = 'admin' }) => {
  // State management
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMemories, setSelectedMemories] = useState(new Set());
  const [categoryCounts, setCategoryCounts] = useState({});
  const [allMemoriesCount, setAllMemoriesCount] = useState(0);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // UI state
  const [viewMode, setViewMode] = useState('list'); // 'cards' | 'list'
  const [expandedMemories, setExpandedMemories] = useState(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [modalType, setModalType] = useState(null); // 'create', 'edit', 'view', 'delete'

  // Fetch memories with filters
  const fetchMemories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const options = {
        search: searchTerm || null,
        category: categoryFilter !== 'all' ? categoryFilter : null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        sortBy,
        sortOrder,
        page: currentPage,
        pageSize
      };

      const result = await getUserMemories(userId, options);

      if (result.error) {
        throw new Error(result.error);
      }

      setMemories(result.data || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
      setCategoryCounts(result.categoryCounts || {});
      setAllMemoriesCount(result.allMemoriesCount || 0);
    } catch (err) {
      console.error('Error fetching memories:', err);
      setError(err.message || 'Failed to fetch memories');
    } finally {
      setLoading(false);
    }
  }, [userId, searchTerm, categoryFilter, dateFrom, dateTo, sortBy, sortOrder, currentPage, pageSize]);

  // Initial fetch and when dependencies change
  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, categoryFilter, dateFrom, dateTo]);

  // Handle memory selection
  const handleSelectMemory = (memoryId) => {
    const newSelected = new Set(selectedMemories);
    if (newSelected.has(memoryId)) {
      newSelected.delete(memoryId);
    } else {
      newSelected.add(memoryId);
    }
    setSelectedMemories(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedMemories.size === memories.length) {
      setSelectedMemories(new Set());
    } else {
      setSelectedMemories(new Set(memories.map(memory => memory.memory_id)));
    }
  };

  // Handle memory expansion
  const toggleMemoryExpansion = (memoryId) => {
    const newExpanded = new Set(expandedMemories);
    if (newExpanded.has(memoryId)) {
      newExpanded.delete(memoryId);
    } else {
      newExpanded.add(memoryId);
    }
    setExpandedMemories(newExpanded);
  };

  // Modal actions
  const handleCreateMemory = () => {
    setSelectedMemory(null);
    setModalType('create');
  };

  const handleEditMemory = (memory) => {
    setSelectedMemory(memory);
    setModalType('edit');
  };

  const handleViewMemory = (memory) => {
    setSelectedMemory(memory);
    setModalType('view');
  };

  const handleDeleteMemory = (memory) => {
    setSelectedMemory(memory);
    setModalType('delete');
  };

  const handleBulkDelete = () => {
    if (selectedMemories.size === 0) return;
    setModalType('bulkDelete');
  };

  const closeModal = () => {
    setSelectedMemory(null);
    setModalType(null);
  };

  // Handle export
  const handleExport = () => {
    try {
      const selectedMemoriesData = memories.filter(memory => selectedMemories.has(memory.memory_id));
      const dataToExport = selectedMemoriesData.length > 0 ? selectedMemoriesData : memories;
      
      downloadMemoriesCSV(dataToExport, `user_${userId}_memories.csv`);
    } catch (err) {
      setError('Failed to export memories: ' + err.message);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  if (loading && memories.length === 0) {
    return (
      <div className="space-y-4">
        {/* Skeleton loading */}
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Memories ({totalCount} total)
          </h3>
          {dashboardMode !== 'client' && (
            <p className="text-sm text-gray-600">
              View and manage all memories for this user
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              ☰ List
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'cards'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              📊 Cards
            </button>
          </div>

          {selectedMemories.size > 0 && (
            <>
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                🗑️ Delete ({selectedMemories.size})
              </button>
              <button
                onClick={handleExport}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                📊 Export ({selectedMemories.size})
              </button>
            </>
          )}
          
          <button
            onClick={handleCreateMemory}
            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ➕ Add Memory
          </button>
          
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search memories..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="all">All Categories</option>
              {Object.entries(MEMORY_CATEGORIES).map(([key, category]) => (
                <option key={key} value={key}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="category-asc">Category A-Z</option>
              <option value="utterance-asc">Content A-Z</option>
            </select>
          </div>
        </div>

        {/* Date Range and Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="From date"
              />
            </div>
            <span className="text-gray-500">to</span>
            <div>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="To date"
              />
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear Filters
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            {viewMode === 'cards' && (
              <div>
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectedMemories.size === memories.length && memories.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="selectAll" className="ml-2 text-sm text-gray-700">
                  Select All
                </label>
              </div>
            )}
            <div className="text-sm text-gray-600">
              {memories.length} of {totalCount} memories
            </div>
          </div>
        </div>

        {/* Category Quick Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              categoryFilter === 'all'
                ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
            }`}
          >
            📋 All ({allMemoriesCount})
          </button>
          {Object.entries(MEMORY_CATEGORIES).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key)}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                categoryFilter === key
                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              {category.icon} {category.label} ({categoryCounts[key] || 0})
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Memories Display */}
      {memories.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">🧠</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Memories Found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || categoryFilter !== 'all' || dateFrom || dateTo
              ? 'No memories match your current filters.'
              : 'This user hasn\'t created any memories yet.'}
          </p>
          <button
            onClick={handleCreateMemory}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ➕ Create First Memory
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <MemoryCardsView 
              memories={memories}
              selectedMemories={selectedMemories}
              expandedMemories={expandedMemories}
              onSelectMemory={handleSelectMemory}
              onToggleExpansion={toggleMemoryExpansion}
              onEditMemory={handleEditMemory}
              onDeleteMemory={handleDeleteMemory}
              onViewMemory={handleViewMemory}
            />
          ) : (
            <MemoryListView
              memories={memories}
              selectedMemories={selectedMemories}
              onSelectMemory={handleSelectMemory}
              onSelectAll={handleSelectAll}
              onEditMemory={handleEditMemory}
              onDeleteMemory={handleDeleteMemory}
              onViewMemory={handleViewMemory}
            />
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1); // Reset to first page when changing page size
            }}
          />
        </>
      )}

      {/* Modals */}
      {modalType && (
        <MemoryEditModal
          type={modalType}
          memory={selectedMemory}
          selectedMemories={selectedMemories}
          userId={userId}
          onClose={closeModal}
          onEdit={(memory) => {
            setSelectedMemory(memory);
            setModalType('edit');
          }}
          onSuccess={() => {
            fetchMemories();
            onRefresh();
            closeModal();
          }}
        />
      )}
    </div>
  );
};

// Shown on a memory that a later correction replaced. The newer text is shown
// inline because the list is paginated and the newer memory is often on
// another page.
const ReplacedBadge = ({ memory }) => {
  const info = getReplacedInfo(memory);
  if (!info) return null;
  return (
    <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
      <span className="font-semibold">Replaced</span>
      {info.replacementText ? <> by: “{info.replacementText}”</> : ' by a newer memory'}
    </div>
  );
};

// Memory Cards View Component
const MemoryCardsView = ({ 
  memories, 
  selectedMemories, 
  expandedMemories, 
  onSelectMemory, 
  onToggleExpansion, 
  onEditMemory, 
  onDeleteMemory, 
  onViewMemory 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {memories.map((memory) => {
        const categoryInfo = getCategoryInfo(memory.category);
        const entities = extractEntitiesFromMemory(memory);
        const sentiment = getMemorySentiment(memory);
        const isExpanded = expandedMemories.has(memory.memory_id);
        const isSelected = selectedMemories.has(memory.memory_id);

        return (
          <div
            key={memory.memory_id}
            className={`relative bg-white rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md ${
              isSelected ? 'ring-2 ring-indigo-500 border-indigo-200' : 'border-gray-200'
            }`}
          >
            {/* Category stripe */}
            <div className={`h-2 rounded-t-lg ${categoryInfo.color.replace('text-', 'bg-').replace('border-', '').split(' ')[0]}`} />

            <div className="p-4">
              {/* Header with integrated checkbox */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {/* Selection checkbox integrated into header */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectMemory(memory.memory_id)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-lg">{categoryInfo.icon}</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${categoryInfo.color}`}>
                    {categoryInfo.label}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => onEditMemory(memory)}
                    className="text-gray-400 hover:text-blue-600 focus:outline-none"
                    title="Edit memory"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => onDeleteMemory(memory)}
                    className="text-gray-400 hover:text-red-600 focus:outline-none"
                    title="Delete memory"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {memory.utterance}
                </p>
                <ReplacedBadge memory={memory} />

                {/* Entities */}
                {entities.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {entities.slice(0, 3).map((entity, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                      >
                        {entity.type}: {entity.value}
                      </span>
                    ))}
                    {entities.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                        +{entities.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-500">
                    {formatMemoryDate(memory.created_at)}
                  </span>
                  {sentiment && (
                    <span className={`text-xs ${sentiment.color}`}>
                      {sentiment.label}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Memory List View Component
const MemoryListView = ({ 
  memories, 
  selectedMemories, 
  onSelectMemory, 
  onSelectAll, 
  onEditMemory, 
  onDeleteMemory, 
  onViewMemory 
}) => {
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedMemories.size === memories.length && memories.length > 0}
                  onChange={onSelectAll}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Content
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {memories.map((memory) => {
              const categoryInfo = getCategoryInfo(memory.category);
              const isSelected = selectedMemories.has(memory.memory_id);

              return (
                <tr key={memory.memory_id} className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}>
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectMemory(memory.memory_id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${categoryInfo.color}`}>
                      {categoryInfo.icon} {categoryInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">
                      {memory.utterance}
                    </div>
                    <div className="mt-1">
                      <ReplacedBadge memory={memory} />
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatMemoryDate(memory.created_at)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => onEditMemory(memory)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit memory"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => onDeleteMemory(memory)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete memory"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemoriesTab;
